import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, ShieldCheck, CheckCircle2, ChevronRight, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { organizationService } from "../../../services/api/organization";
import MSAContentNoSignature from "../master-service-agreement/MSAContentNoSignature";
import SlaContentNoSignature from "../sla/SlaContentNoSignature";

const PostOnboardAgreementPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id") || searchParams.get("id");

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientName, setClientName] = useState("[Client Name]");
  const [tierName, setTierName] = useState("");
  const [representativeName, setRepresentativeName] = useState("[Representative Name]");
  const [representativeRole, setRepresentativeRole] = useState("[Representative Role]");
  
  const [activeTab, setActiveTab] = useState<"msa" | "sla">("msa");
  const [msaChecked, setMsaChecked] = useState(false);
  const [slaChecked, setSlaChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const today = format(new Date(), "do MMMM, yyyy");

  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const response = await organizationService.get(id);
        if (response?.data) {
          const org = response.data;
          setClientName(org.name || "[Client Name]");
          setRepresentativeName(org.representativeName || "[Representative Name]");
          setRepresentativeRole(org.representativeRole || "[Representative Role]");
          
          if (org.subscriptionTier) {
            setTierName(org.subscriptionTier);
          } else if (org.preselectedPackages && org.preselectedPackages.length > 0) {
            setTierName(org.preselectedPackages[0].tier);
          }
        }
      } catch (error) {
        console.error("Failed to load organization details", error);
        toast.error("Failed to load organization details.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrgDetails();
  }, [id]);

  const handleSubmit = async () => {
    if (!id) {
      toast.error("Organization ID is missing.");
      return;
    }
    if (!msaChecked || !slaChecked) {
      toast.error("Please accept both agreements to proceed.");
      return;
    }

    setIsSubmitting(true);
    try {
      await organizationService.acceptPostOnboardDocuments(id);
      setIsSuccess(true);
      toast.success("Agreements signed and accepted successfully!");
    } catch (error: any) {
      console.error("Error accepting agreements:", error);
      toast.error(error?.response?.data?.message || "Failed to submit agreements.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
        <p className="text-slate-400 font-medium">Loading updated agreements...</p>
      </div>
    );
  }

  if (!id) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white px-4">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h1 className="text-xl font-bold mb-2">Invalid Link</h1>
        <p className="text-slate-400 text-center max-w-md">
          This link is invalid or missing required details. Please check the email invitation and try again.
        </p>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 relative overflow-hidden font-abel font-sans">
        {/* Decorative background blur blobs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-900/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-900/20 rounded-full blur-[100px]" />
        
        <div className="max-w-md w-full relative z-10 bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 text-center shadow-2xl animate-in fade-in zoom-in duration-500">
          <div className="w-20 h-20 bg-green-500/10 border border-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Agreements Signed!</h1>
          <p className="text-slate-400 leading-relaxed mb-8">
            Thank you! The updated agreements have been successfully signed and accepted. A notification has been sent to our operations team. You can close this window.
          </p>
          <div className="text-xs text-slate-500 border-t border-slate-800 pt-4">
            Signed on behalf of {clientName} by {representativeName}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-8 px-4 font-abel font-sans relative overflow-hidden">
      {/* Decorative background blur blobs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-900/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-900/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10 pb-36">
        {/* Header Block */}
        <div className="text-center mb-8">
          <span className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-xs font-semibold text-blue-400 uppercase tracking-wider">
            Required Update
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-2">
            Review updated agreement for {clientName}
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto">
            Please review the updated agreements below for the <span className="text-blue-400 font-bold">{tierName || "Selected"}</span> tier subscription pack. Check both consent boxes to complete the signing process.
          </p>
        </div>

        {/* Tab Controls */}
        <div className="flex border-b border-slate-800 mb-6 max-w-md mx-auto">
          <button
            onClick={() => setActiveTab("msa")}
            className={`flex-1 py-3 text-center font-medium border-b-2 transition-all flex items-center justify-center gap-2 ${
              activeTab === "msa"
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <FileText className="w-4 h-4" />
            Master Service Agreement
          </button>
          <button
            onClick={() => setActiveTab("sla")}
            className={`flex-1 py-3 text-center font-medium border-b-2 transition-all flex items-center justify-center gap-2 ${
              activeTab === "sla"
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            Service Level Agreement
          </button>
        </div>

        {/* Active Document Viewer */}
        <div className="bg-slate-900/60 backdrop-blur-md rounded-2xl border border-slate-800/80 overflow-hidden shadow-xl max-h-[60vh] overflow-y-auto custom-scrollbar p-1">
          {activeTab === "msa" ? (
            <MSAContentNoSignature
              clientName={clientName}
              representativeName={representativeName}
              representativeRole={representativeRole}
              date={today}
              effectiveDate={today}
              readOnly={true}
            />
          ) : (
            <SlaContentNoSignature
              clientName={clientName}
              representativeName={representativeName}
              representativeRole={representativeRole}
              date={today}
              readOnly={true}
              uploadedSignature={null}
              signaturePreview={null}
              onFileUpload={() => {}}
              onRemoveSignature={() => {}}
            />
          )}
        </div>

        {/* Floating Consent Footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-slate-900/90 backdrop-blur-lg border-t border-slate-800/80 p-4 z-40 shadow-2xl">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer group select-none">
                <input
                  type="checkbox"
                  checked={msaChecked}
                  onChange={(e) => setMsaChecked(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-slate-700 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-900 cursor-pointer accent-blue-500"
                />
                <span className="text-sm text-slate-300 group-hover:text-slate-100 transition-colors">
                  I hereby review and accept the Master Service Agreement (MSA) updates on behalf of <strong className="text-white">{clientName}</strong>.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group select-none">
                <input
                  type="checkbox"
                  checked={slaChecked}
                  onChange={(e) => setSlaChecked(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-slate-700 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-900 cursor-pointer accent-blue-500"
                />
                <span className="text-sm text-slate-300 group-hover:text-slate-100 transition-colors">
                  I hereby review and accept the Service Level Agreement (SLA) updates on behalf of <strong className="text-white">{clientName}</strong>.
                </span>
              </label>
            </div>

            <div className="flex-shrink-0 flex items-center gap-3">
              {activeTab === "msa" ? (
                <button
                  onClick={() => setActiveTab("sla")}
                  className="px-5 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 font-semibold text-sm transition-all flex items-center gap-1.5 border border-slate-700 text-white"
                >
                  Next: SLA Update
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : null}

              <button
                onClick={handleSubmit}
                disabled={!msaChecked || !slaChecked || isSubmitting}
                className={`px-8 py-3.5 rounded-xl font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2 ${
                  msaChecked && slaChecked && !isSubmitting
                    ? "bg-blue-600 hover:bg-blue-500 text-white hover:shadow-blue-500/20 active:scale-[0.98]"
                    : "bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-800"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  <>
                    Sign & Accept Agreements
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostOnboardAgreementPage;
